var hash = require("object-hash").default;
import plansServices from "../services/plans";
import priceServices from "../services/price";
import { toThousandFilter } from "./filters";
import store from "../store";
import { i18n } from "./i18n";
import { formatPrice, defaultDisks, defaultBandwidths } from "./price";
import { getNewEndpoint } from "../config/newEndpoint";

const postPlanNameCache = new Map();
// Price flavor
function formatPlanName(data) {
  const [type, value] = data;
  let listPlan = [];
  if (type === "server") {
    listPlan = value.map(({ plan_names, category_code, resource_type }) => ({
      plan_name: plan_names?.on,
      category_code,
      resource_type,
    }));
  } else {
    listPlan = value.flat().map(({ plan_name, category_code }) => ({
      plan_name,
      category_code,
    }));
  }
  const uniquePlans = Array.from(new Set(listPlan.map(JSON.stringify))).map(
    JSON.parse
  );
  return uniquePlans;
}

export async function fetchPlanName(data) {
  try {
    store.price.state.loadingPlan = true;
    const NEW_ENDPOINT = await getNewEndpoint();
    const url = NEW_ENDPOINT?.PLANS;
    const key = `${url}:${hash(data, {
      unorderedObjects: true,
      ignoreUnknown: true,
    })}`;
    if (!postPlanNameCache.get(key)) {
      postPlanNameCache.set(key, { status: "loading" });
    } else {
      while (postPlanNameCache.get(key)?.status === "loading") {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 100));
      }
      store.price.state.loadingPlan = false;
      return postPlanNameCache.get(key)?.result;
    }
    const response = await plansServices.getPlans(data);
    const formatRes = Object.entries(response).map(formatPlanName).flat();
    const type = Object.keys(response)?.[0];
    const test = type === "server" ? response?.[type] : response?.[type].flat();
    if (type === "server" || type === "volumes" || type === "backups") {
      const formatPlan = test?.reduce((acc, item) => {
        let stringId = `${item?.billing_plan}_${item?.category}_${item?.generation_id}`;
        if (type === "volumes") {
          stringId = `${item?.category}_${item?.volume_type}_${item?.billing_plan}`;
        }
        if (type === "backups") {
          stringId = `${item?.category}_${item?.volume_type}_${item?.billing_plan}_backup`;
        }
        const test = { ...item };
        acc[`${stringId}`] = acc[`${stringId}`] || [];
        acc[`${stringId}`].push(test);
        return acc;
      }, {});
      store.price.state.planName = {
        ...store.price.state.planName,
        ...formatPlan,
      };
    } else {
      const formatPlan = test?.reduce((acc, item) => {
        const test = { ...item };
        acc[`${type}`] = acc[`${type}`] || [];
        acc[`${type}`].push(test);
        return acc;
      }, {});
      store.price.state.planName = {
        ...store.price.state.planName,
        ...formatPlan,
      };
    }
    postPlanNameCache.set(key, { status: "loaded", result: formatRes });
    store.price.state.loadingPlan = false;
    return formatRes;
  } catch (error) {
    console.error(error);
    store.price.state.loadingPlan = false;
    return [];
  }
}

export async function fetchPriceFlavor(planNames, billingPlan) {
  try {
    const params = planNames.map((plan) => ({
      plan_name: plan?.plan_name,
      quantity: (billingPlan === "spot_instance" ? 3600 : plan?.quantity) ?? 1,
      category_code: plan?.category_code,
    }));
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    const formatPrice = response?._items?.reduce((acc, item) => {
      const { plan_name: planName, ...rest } = item;
      acc[`${planName}`] = acc[`${planName}`] || [];
      acc[`${planName}`].push(rest);
      return acc;
    }, {});
    store.price.state.price = { ...store.price.state.price, ...formatPrice };
    store.price.state.loadingPrice = false;
  } catch (error) {
    store.price.state.loadingPrice = false;
  }
}
export function getFlavorPlanName(
  category,
  cpu,
  gen,
  ram,
  billingPlan,
  listPlanName,
  disk
) {
  if (Array.isArray(listPlanName) && listPlanName.length) {
    const flavorInfo = listPlanName.find((item) => {
      const matchesBillingPlan = item?.billing_plan === billingPlan;
      const matchesCategory = item?.category === category;
      const matchesCpu = item?.vcpus === cpu;
      const matchesRam = item?.ram === ram;
      const matchesDisk = disk ? item?.disk === disk : true;
      const matchesGeneration = item?.generation_id === gen;
      return (
        matchesBillingPlan &&
        matchesCategory &&
        matchesCpu &&
        matchesRam &&
        matchesDisk &&
        matchesGeneration
      );
    });
    return flavorInfo?.plan_names?.on || "";
  }
  return "";
}

export function getFlavorCost(planName) {
  if (planName !== "") {
    const currentFlavor = store?.price?.state?.price[planName];
    return currentFlavor?.[0]?.amount ?? 0;
  }
  return 0;
}

export function calculationPriceFlavor(planName, billingPlan) {
  const tempPriceFlavor = getFlavorCost(planName);
  const priceFlavor =
    billingPlan === "spot_instance" ? tempPriceFlavor / 3600 : tempPriceFlavor;
  const priceFlavorDisplay = `${toThousandFilter(priceFlavor)} đ/${i18n.t(
    billingPlan === "on_demand"
      ? "shared.hour"
      : billingPlan === "spot_instance"
      ? "shared.second"
      : "shared.month"
  )}`;
  return {
    priceFlavor,
    priceFlavorDisplay,
  };
}

// Price disk
function formatParamsDisk(category, planName, arrDisks = defaultDisks) {
  const params = arrDisks.map((disk) => ({
    plan_name: planName,
    category_code: category,
    quantity: disk,
  }));
  return params;
}

export async function fetchDiskPrice(category, planName, arrDisks) {
  try {
    if (planName === "") return;
    const params = [...formatParamsDisk(category, planName, arrDisks)];
    store.price.state.loadingCustomPrice = true;
    const response = await priceServices.getPrice(params);
    if (!(planName in store.price.state.price)) {
      store.price.state.price[planName] = [...response?._items];
    } else {
      store.price.state.price[planName].push(...response?._items);
    }
    store.price.state.loadingCustomPrice = false;
  } catch (error) {
    store.price.state.loadingCustomPrice = false;
  }
}

export function getDiskCost({ size, type, billingPlan, category, planName }) {
  if (
    planName === "" ||
    ((category === "vps" || category === "dedicated") &&
      billingPlan !== "saving_plan")
  ) {
    return 0;
  }
  if (!size || !type || !category || planName === "") return 0;
  const currentDisk = store?.price?.state?.price?.[planName]?.find(
    (disk) => disk?.quantity === size
  );
  if (!currentDisk && !store?.price?.state?.loadingCustomPrice) {
    fetchDiskPrice(category, planName, [size]);
  }
  return currentDisk?.amount ?? 0;
}

export function getVolumePlanName(
  category,
  billingPlan,
  volumeType,
  isBackUp = false
) {
  if (category === "" || billingPlan === "" || volumeType === "") return "";
  const newBillingPlan =
    billingPlan === "spot_instance" ? "on_demand" : billingPlan;
  const stringId = isBackUp
    ? `${category}_${volumeType}_${newBillingPlan}_backup`
    : `${category}_${volumeType}_${newBillingPlan}`;
  if (store?.price?.state?.planName[`${stringId}`] !== undefined) {
    const volumeInfo = store?.price?.state?.planName[`${stringId}`].find(
      (item) =>
        item?.billing_plan === newBillingPlan &&
        item?.category === category &&
        item?.volume_type === volumeType
    );
    if (volumeInfo !== undefined) {
      return volumeInfo;
    }
  } else {
    const planData = {
      volumes: [
        {
          volume_type: volumeType,
          billing_plan: newBillingPlan,
          category: category,
        },
      ],
    };
    fetchPlanName(planData);
  }
  return "";
}

export function calculationPriceDisk({
  size,
  type,
  billingPlan,
  category,
  planName,
}) {
  const priceDisk = getDiskCost({
    size,
    type,
    billingPlan,
    category,
    planName,
  });
  const priceDiskDisplay = `${toThousandFilter(priceDisk)} đ/${i18n.t(
    billingPlan === "on_demand" || billingPlan === "spot_instance"
      ? "shared.hour"
      : "shared.month"
  )}`;
  return {
    priceDisk,
    priceDiskDisplay,
  };
}

// Price bandwidth
export async function fetchPriceBandwidth(
  planName,
  arrBandwidths = defaultBandwidths,
  customCost = false
) {
  const { price } = store?.price?.state;
  if (Object.keys(price).includes(planName) && !customCost) return;

  try {
    const params = arrBandwidths.map((bandwidth) => ({
      plan_name: planName,
      category_code: "default",
      quantity: bandwidth,
    }));
    store.price.state.loadingCustomPrice = true;
    const response = await priceServices.getPrice(params);
    store.price.state.loadingCustomPrice = false;
    if (!Object.keys(price).includes(planName)) {
      price[planName] = [...response?._items];
    } else {
      price[planName].push(...response?._items);
    }
  } catch (error) {
    store.price.state.loadingCustomPrice = false;
  }
}
export function getCostBandwidth(size, planName) {
  if (!size || size < 0) return 0;
  const currentBandwith = store?.price?.state?.price?.[planName]?.find(
    (bandwidth) => bandwidth?.quantity === size
  );
  if (!currentBandwith && !store.price.state.loadingCustomPrice) {
    fetchPriceBandwidth(planName, [size], true);
  }
  return currentBandwith?.amount ?? 0;
}

export function filterBandwidthPrice(size, planName) {
  let price = "";
  const block = 1000;
  if (size / block === 100 || !size) {
    price = i18n.t("iaasCloud.quotas.free");
  } else if (size / block > 100) {
    price = formatPrice(getCostBandwidth(size / block - 100, planName));
  }
  return price;
}

// Price Wanip
export async function getWanipPrice(planName) {
  const { price } = store?.price?.state;
  if (Object.keys(price).includes(planName)) return;

  try {
    const params = {
      plan_name: planName,
      category_code: "default",
      quantity: 1,
    };
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    const formatPrice = {
      [planName]: response?._items,
    };
    store.price.state.price = { ...price, ...formatPrice };
    store.price.state.loadingPrice = false;
  } catch (error) {
    store.price.state.loadingPrice = false;
  }
}

// Price snapshot, backup
export async function fetchSnapshotAndBackupPrice(size, planName, category) {
  try {
    const params = {
      plan_name: planName,
      category_code: category,
      quantity: size,
    };
    store.price.state.loadingPrice = true;
    const response = await priceServices.getPrice(params);
    if (!(planName in store.price.state.price)) {
      store.price.state.price[planName] = [...response?._items];
    } else {
      store.price.state.price[planName].push(...response?._items);
    }
    store.price.state.loadingPrice = false;
  } catch (error) {
    store.price.state.loadingPrice = false;
  }
}

export function getPriceSnapshotAndBackup({ size, type, planName, category }) {
  if (
    !size ||
    !type ||
    planName === "" ||
    !planName ||
    category === "" ||
    !category
  )
    return 0;
  const currentPlanName = store?.price?.state?.price?.[planName]?.find(
    (disk) => disk?.quantity === size
  );

  if (!currentPlanName && !store?.price?.state?.loadingPrice) {
    fetchSnapshotAndBackupPrice(size, planName, category);
  }
  return currentPlanName?.amount ?? 0;
}
